import { LegalPage, Section, SubSection } from "./LegalPage";

export function Privacy() {
  return (
    <LegalPage title="Privacy Policy" updatedOn="April 23, 2026">
      <p>
        This Privacy Policy describes how Proofly, Inc. ("<strong>Proofly</strong>")
        collects, uses, and shares personal information when you use our
        Service at proofly.one.
      </p>

      <Section title="1. The short version">
        <ul>
          <li>We collect what we need to run the Service — no behavioral advertising, ever.</li>
          <li>We don't sell personal information.</li>
          <li>We don't train foundation models on your private Customer Data.</li>
          <li>You can export or delete your data at any time.</li>
          <li>Infrastructure is SOC 2-aligned and hosted with audited providers.</li>
        </ul>
      </Section>

      <Section title="2. What we collect">
        <SubSection title="2.1 Account information">
          Name, work email, organization name, and a password or federated
          identity (e.g. Google). Optional profile data you add to Settings.
        </SubSection>
        <SubSection title="2.2 Customer Data">
          Receipts, invoices, mileage entries, transaction records, vendor
          profiles, categories, policies, approvals, and related context you
          upload or that we ingest on your direction.
        </SubSection>
        <SubSection title="2.3 Usage information">
          Pages viewed, features used, device and browser type, approximate
          location from IP, and error diagnostics. We use{" "}
          <a href="https://plausible.io/privacy-focused-web-analytics" target="_blank" rel="noreferrer">Plausible</a>,
          a privacy-respecting analytics tool that does not set cookies or
          track individuals across sites.
        </SubSection>
        <SubSection title="2.4 Integrations">
          When you connect a third party (QuickBooks, Xero, Stripe, Plaid,
          Gmail, etc.), we receive only the data you authorize for the scope
          you chose. You can revoke at any time from Settings.
        </SubSection>
      </Section>

      <Section title="3. How we use information">
        <ul>
          <li>Provide and operate the Service;</li>
          <li>Categorize, extract, and reconcile records using AI models;</li>
          <li>Detect anomalies and policy violations;</li>
          <li>Respond to support requests;</li>
          <li>Improve the Service in aggregated, de-identified form;</li>
          <li>Send transactional email (billing, security, service updates);</li>
          <li>Comply with law and protect rights.</li>
        </ul>
        <p>
          We don't use personal information for behavioral advertising, and we
          don't rent or sell it.
        </p>
      </Section>

      <Section title="4. AI & your data">
        <p>
          Some features use AI models (our own and licensed third-party
          models) to extract fields, suggest categories, and flag anomalies.
          We do not allow AI vendors to train foundation models on your
          private Customer Data. We process prompts and model output only to
          deliver the Service to you.
        </p>
      </Section>

      <Section title="5. How we share">
        <SubSection title="5.1 Service providers">
          We use vetted infrastructure providers (e.g. Supabase/Postgres, AWS,
          Sentry, Plausible, email delivery). They process data only on our
          instructions and under written agreements.
        </SubSection>
        <SubSection title="5.2 At your direction">
          When you connect a third-party integration or share a read-only
          auditor link, data flows to those parties under your direction.
        </SubSection>
        <SubSection title="5.3 Legal & safety">
          We may share information to comply with law, respond to valid legal
          process, or protect rights, safety, or property.
        </SubSection>
        <SubSection title="5.4 Business transfers">
          In a merger, acquisition, or sale, personal information may transfer
          to the successor, subject to this Policy.
        </SubSection>
      </Section>

      <Section title="6. Retention">
        <p>
          We retain Customer Data for as long as your account is active, plus
          30 days after termination to allow export. Free plan keeps records
          for 1 year on a rolling basis; Team keeps them for 7 years;
          Enterprise is configurable. We may retain limited records longer to
          comply with law or resolve disputes.
        </p>
      </Section>

      <Section id="security" title="7. Security">
        <p>
          We host the Service on SOC 2-aligned infrastructure and apply
          controls including encryption in transit (TLS 1.2+) and at rest,
          least-privilege access, audit logging of admin actions, rate
          limiting, and continuous vulnerability monitoring. No system is
          perfectly secure; if we discover a breach affecting your data,
          we'll notify affected customers as required by law.
        </p>
      </Section>

      <Section title="8. Your rights">
        <p>
          Depending on where you live, you may have rights to access, correct,
          delete, export, or restrict use of your personal information. You
          can exercise most of these in-product:
        </p>
        <ul>
          <li><strong>Access/Export</strong>: Settings → Exports.</li>
          <li><strong>Delete account</strong>: Settings → Danger zone.</li>
          <li><strong>Opt out of analytics</strong>: Settings → Privacy.</li>
        </ul>
        <p>
          You can also email{" "}
          <a href="mailto:privacy@proofly.one">privacy@proofly.one</a> at any
          time.
        </p>
      </Section>

      <Section title="9. International transfers">
        <p>
          Proofly is operated from the United States. If you access the
          Service from outside the US, your data will be transferred to and
          processed in the US under appropriate safeguards.
        </p>
      </Section>

      <Section title="10. Children">
        <p>
          Proofly isn't intended for children under 18, and we don't knowingly
          collect data from them.
        </p>
      </Section>

      <Section title="11. Changes">
        <p>
          We'll update this Policy from time to time. If changes are material,
          we'll post notice in the app at least 30 days before they take
          effect.
        </p>
      </Section>

      <Section title="12. Contact">
        <p>
          Questions about this Policy?{" "}
          <a href="mailto:privacy@proofly.one">privacy@proofly.one</a>
        </p>
      </Section>
    </LegalPage>
  );
}
